/**
 * Deep-link target resolution by walking UP the entity hierarchy, split out of
 * `entity-scope-resolution.ts` (DV-3013 PR 4, spec §4).
 *
 * The rationale for walking rather than scanning, and for not using
 * `GET /entities/root/{uuid}`, is on `resolveViaAncestorWalk` below — it is the
 * reason this is 1–2 requests instead of ~1,689.
 */
import { fetchEntityDocRequest } from './entity-scope-api';
import type { SwitchEntityTarget } from './entity-scope';
import type { AccessibleOrg } from '../types/org-selection.types';
import type { EntityHierarchy } from '../types/order.types';

/**
 * Hop cap for the ancestor walk. Prod's deepest tree is 1 level, so this is
 * pure defence — and it is not theoretical: DV-3044 records a prod entity
 * whose `parentEntityUuid` matches no entity, and entity-service's own
 * `getRootEntityUUID` added a visited set (DV-3019) because a cycle would hang
 * a request thread per photoset delivery. Matches
 * `CrossTenantReadAuditor.MAX_DEPTH`.
 */
export const MAX_ANCESTOR_HOPS = 10;

const isReachable = (org: AccessibleOrg | undefined): org is AccessibleOrg =>
  !!org && !org.disabled;

/**
 * Resolve a deep-link target by walking UP from it until an ancestor the user
 * can reach appears in `orgs` (spec §4.4 / D11).
 *
 * Replaces `resolveEntityViaTrees`, which iterated every `OrgData` entry and
 * fetched each one's whole tree, sequentially, until it found the target. For
 * an internal admin that is ~1,689 round trips — tolerable behind a mounted
 * picker, indefensible in the bootstrap, where it blocks a blank screen.
 *
 * `GET /entities/root/{uuid}` is deliberately NOT used: `OrgData` can hold a
 * mid-tree entity, so a user who belongs to child C but not to root P would be
 * falsely denied a link to C's own child. Walking up preserves the old
 * semantics exactly — *find an ancestor this user can reach* — in 1–2 calls.
 *
 * @returns a switch target, or null when no ancestor is reachable (which the
 *   caller maps to `entity-access`). A failure fetching the target document
 *   THROWS instead, because "the lookup broke" is a different outcome from
 *   "you may not see this".
 */
export async function resolveViaAncestorWalk(
  targetId: string,
  orgs: AccessibleOrg[],
): Promise<SwitchEntityTarget | null> {
  // Not caught: a broken target lookup must surface as 'unknown'.
  const targetDoc: EntityHierarchy = await fetchEntityDocRequest(targetId);

  const byId = new Map(orgs.map((org) => [org.entityId, org]));
  const target = {
    entityId: targetId,
    entityName: targetDoc.entityName,
  };

  // Depth 0: the target is itself an accessible org.
  const self = byId.get(targetId);
  if (isReachable(self)) {
    return { ...target, role: self.role, parentEntityId: targetId };
  }

  const visited = new Set<string>([targetId]);
  let cursor = targetDoc.parentEntityUuid;

  for (let hop = 0; hop < MAX_ANCESTOR_HOPS && cursor; hop += 1) {
    if (visited.has(cursor)) {
      console.warn(
        `Entity scope: parent cycle at ${cursor} while resolving ${targetId}`,
      );
      return null;
    }
    visited.add(cursor);

    const ancestor = byId.get(cursor);
    if (isReachable(ancestor)) {
      // The reachable ancestor becomes parentEntityId — the entity-tree fetch
      // key and Prepay & Save anchor — NOT the tree root.
      return { ...target, role: ancestor.role, parentEntityId: cursor };
    }

    try {
      const doc = await fetchEntityDocRequest(cursor);
      cursor = doc.parentEntityUuid;
    } catch {
      // An orphan parentEntityUuid (DV-3044) or a transient miss. The target
      // itself resolved, so this is "no reachable ancestor", not a broken
      // lookup.
      return null;
    }
  }

  return null;
}
